/**
 * The "Copy diagnostic" text (REQUIREMENTS.md §10, prompt 0.8.0 item 5): a Markdown block Elijah pastes
 * into Claude Code as the next prompt — the fixed preamble, the check, the error text, the last 20
 * relevant log lines (already redacted by the API), the app version and the environment name. The API
 * builds the bundle (GET /api/admin/diagnostics); this renders it, the same on the server and the page.
 * A last guard refuses anything that looks like a secret value in the configuration block.
 */
import type { DiagnosticBundle } from './admin';

const SECRETISH = /(secret|key|token|password)/i;

export function diagnosticMarkdown(b: DiagnosticBundle): string {
  const bad = b.checks.filter((c) => c.status !== 'green');
  const count = (s: string) => b.checks.filter((c) => c.status === s).length;
  const L: string[] = [];
  L.push(b.preamble);
  L.push('');
  L.push('```text');
  L.push(`ABF Scheduler — Health diagnostic (from Admin → Health → Copy diagnostic)`);
  L.push(`Checked: ${b.generatedAt}`);
  L.push(`Environment: ${b.environment}`);
  L.push(`App ${b.version.app} · ${b.version.sha.slice(0, 7)} · deployed ${b.version.builtAt} · Node ${b.version.node}`);
  L.push(`Result: ${count('green')} green, ${count('amber')} amber, ${count('red')} red`);
  L.push('');
  if (b.check) {
    L.push(`CHECK ${b.check.status === 'green' ? 'OK' : 'FAILED'}: ${b.check.key} [${b.check.status.toUpperCase()}]`);
    L.push(`  ${b.check.message}`);
    if (b.check.detail) for (const d of b.check.detail.split('\n')) L.push(`    ${d}`);
    L.push('');
  }
  const others = bad.filter((c) => c.key !== b.check?.key);
  if (others.length) {
    L.push(b.check ? 'OTHER CHECKS NOT GREEN:' : 'WHAT IS NOT GREEN:');
    for (const c of others) {
      L.push(`- [${c.status.toUpperCase()}] ${c.key}: ${c.message}`);
      if (c.detail) for (const d of c.detail.split('\n').slice(0, 8)) L.push(`    ${d}`);
    }
    L.push('');
  } else if (!b.check && !bad.length) {
    L.push('Every check is green.');
    L.push('');
  }
  L.push('CONFIGURATION (no secrets — secret settings show only "set" or "not set"):');
  for (const [k, v] of Object.entries(b.config)) {
    const safe = SECRETISH.test(k) && v !== 'set' && v !== 'not set' ? '(hidden)' : v;
    L.push(`- ${k}: ${safe}`);
  }
  L.push('');
  L.push(`LAST ${b.log.length} RELEVANT LOG LINES (redacted):`);
  if (!b.log.length) L.push('(none recorded yet)');
  for (const l of b.log) L.push(`${l.at} ${l.level.toUpperCase()} ${l.event} ${l.text}`);
  L.push('```');
  return L.join('\n');
}
